export enum ScoreEnum {
  LOW = 1,
  MEDIUM = 2,
  HIGH = 3,
}

// API Types based on the YAML specification
export interface BusinessProfile {
  business_name: string;
  business_address: string;
  business_type: string;
  industry_category: string;
  mission_of_the_company?: string;
  target_audience: string;
  geographic_focus: string;
}

// Legacy service (BusinessInfoResponse)
export interface LegacyService {
  name: string;
  description: string;
  target_audience: string;
  geographic_focus: string;
  unique_id?: string;
}

export interface PageUrl {
  url: string;
  source_type: 'internal' | 'external';
}

export interface SourceSummary {
  total_urls: number;
  internal_urls: number;
  external_urls: number;
  internal_percentage: number;
}

export interface InsightsServiceContext {
  product_identified: ProductIdentified;
  product_extraction: ProductExtraction;
}

// Product Context types for LLM Identification Insights
export interface ProductExplicitStatement {
  quote: string;
  page_url: string;
  context: string;
}

export interface ProductImpliedIndicator {
  signal: string;
  page_url: string;
  reasoning: string;
}

export interface ProductIdentified {
  primary_value: string;
  confidence_level: string;
  evidence_strength: string;
}

export interface ProductExtraction {
  explicit_statements: ProductExplicitStatement[];
  implied_indicators: ProductImpliedIndicator[];
}

// v1 Business Insights service schema
export interface InsightsService {
  name: string;
  description: string;
  target_audience: string;
  geographic_focus: string;
  unique_id?: string;
  competitors?: CompetitorInfo[]; // optional convenience
  context?: InsightsServiceContext;
}

export interface CompetitorInfo {
  name: string;
  domain: string;
  description: string;
}

export interface OptimisationPlan {
  name: string;
  plan: string[];
}

export interface MetricCompetitor {
  domain: string;
  score: number;
  description: string;
  optimisation_plan: string[];
}

export interface MetricDetail {
  score: number;
  description: string;
  optimisation_plan: string[];
  competitors: MetricCompetitor[];
}

export interface Competitor {
  domain: string;
  name: string;
  competitor_for: string[];
  description: string;
}

export interface MetricsBlock {
  visibility: {
    score: MetricDetail;
    mentions: MetricDetail;
    position: MetricDetail;
    queries: string[];
  };
  sentiment: MetricDetail;
  completeness: MetricDetail;
  competitors: Competitor[];
}

export interface MetricsService {
  name: string;
  scope: string[];
  description: string;
  visibility: {
    score: MetricDetail;
    mentions: MetricDetail;
    position: MetricDetail;
    queries: string[];
  };
  sentiment: MetricDetail;
  completeness: MetricDetail;
  competitors: Competitor[];
}

// Request types
export interface BusinessInfoRequest {
  llm: string;
  domain: string;
  lang: string;
  tier: string;
}

export interface BusinessInsightsRequest {
  domain: string;
  reportId?: string; // optional report id for history mode
}

export interface MetricsRequest {
  llm: string;
  domain: string;
  lang: string;
  tier: string;
}

export interface VisibilityScoreRequest {
  domain: string;
  reportId?: string; // optional report id for history mode
}

export interface ChangeProjectRequest {
  hash: string;
}

// Project types
export type ReportStatus = 'BUSY' | 'READY' | 'ERROR';
export enum PaidStatus {
  TRIAL = 0,
  PAID = 1,
  EXPIRED = 2,
  TERMINATED = 3,
  UNASSIGNED = 4,
  NO_LICENSE = 5,
  UNPAID = 99,
}

export enum DomainStatus {
  TRIAL = 0,
  PAID = 1,
  EXPIRED = 2,
  TERMINATED = 3,
  UNASSIGNED = 4,
  NO_LICENSE = 5,
}

export interface ReportInfo {
  id: string;
  lang: string;
  date: string;
  status: ReportStatus;
}

export interface ProjectData {
  id: string;
  userID: string;
  domainname: string;
  paid: PaidStatus;
  date: string;
  domain_state: DomainStatus;
  assign_date: string | null;
  license_id: number | null;
  vision_mode_access_requested: string | null;
  vision_mode_access_granted: string | null;
}
export interface Project {
  paid: PaidStatus;
  domain: string;
  language?: string | null;
  data?: ProjectData;
  reportsAvailable: boolean; // deprecated
  lastReportDate?: string | null;
  reports?: ReportInfo[];
  nextCreateCountdown?: number;
  subscription?: {
    state: SubscriptionState;
    next_billing_date: string;
    active_until: string;
  };
}

export type TestingType = 'A' | 'B';

export interface UserInfoResponse {
  ab_testing: TestingType;
  activeDomains: Project[];
  inactiveDomains: string[];
  csrf?: string;
}

// Response types
export interface BusinessInfoResponse {
  business_profile: BusinessProfile;
  services: InsightsService[];
  page_urls?: PageUrl[];
  source_summary?: SourceSummary;
  statusCode?: number;
}

export interface MetricsResponse {
  business_profile: MetricsBlock;
  services: MetricsService[];
}

// Unified Rule types (Conversational Writing & Statistical Evidence now share structure)
export interface RuleRequest {
  domain: string;
  reportId?: string; // optional report context for history mode
}

export interface RuleExample {
  link: string;
  current_implementation: string;
  improved_version: string;
  improvement_rationale: string;
}

export interface RuleEntity {
  examples: RuleExample[];
  assessment_score?: ScoreEnum;
  assessment_explanation?: string;
}

export interface RuleResponse {
  domain?: string; // optional (some mocks inject domain)
  company: RuleEntity;
  products: Record<string, RuleEntity>;
  statusCode?: number;
}

export interface TechnicalRule {
  rule: string;
  score: number | null;
  statusCode?: number;
}

export interface RobotsBot {
  allowed: boolean;
  blocks_paths: string[];
}

export interface TechRuleFinding {
  score: number;
  max_score: number;
  [key: string]: any; // flexible for various finding details across rules
}

export interface TechnicalRuleResponse extends TechnicalRule {
  findings: Record<string, TechRuleFinding>;
}

export interface RobotsRuleResponse extends TechnicalRule {
  max_score: number;
  findings?: {
    file: {
      exists: boolean | string;
    };
    wildcard: {
      blocks_all: boolean;
      blocks_paths: boolean;
    };
    gpt_bot: RobotsBot;
    claude_bot: RobotsBot;
    google_extended: RobotsBot;
    perplexity_bot: RobotsBot;
    chat_gpt_user: RobotsBot;
  };
}

// Visibility Score (LLM visibility performance) types
export interface VisibilityExecutionDetail {
  execution_id?: number; // optional in sample for competitor/product entries
  mentioned?: boolean;
  rank?: number;
  overall_score: number; // always present in examples
}

export interface VisibilityQueryMetric {
  query: string;
  mention_rate: number; // percent or score
  total_mentions: number;
  total_executions: number;
  mean_rank: number;
  overall_score: number;
  execution_details: VisibilityExecutionDetail[];
}

export interface VisibilityEntityBase {
  domain: string;
  name: string;
  description: string;
  overall_score: number;
  mention_rate: number;
  total_mentions: number;
  mean_rank: number;
  query_metrics: VisibilityQueryMetric[];
}

export interface VisibilityCompanyBlock {
  entity: VisibilityEntityBase;
  competitors: VisibilityEntityBase[];
}

export interface VisibilityProductBlock {
  entity: VisibilityEntityBase;
  competitors: VisibilityEntityBase[];
}

// Root response structure
export interface VisibilityScoreResponse {
  company: VisibilityCompanyBlock;
  products: Record<string, VisibilityProductBlock>;
  statusCode?: number;
}

// Visibility Recommendations types (from recommendations.json sample)
export interface VisibilityRecommendationStrategy {
  priority: number;
  strategy_id: string;
  strategy_name: string;
  strategy_summary: string;
  category_name: string;
  rationale: string;
  implementation_guidance: string;
  expected_impact: string;
}

export interface VisibilityServiceRecommendation {
  service_name: string;
  overall_score: number;
  mention_rate: number;
  mean_rank: number;
  recommendations: VisibilityRecommendationStrategy[];
}

export interface VisibilityRecommendationsResponse {
  domain: string;
  company_name: string;
  overall_score: number;
  mention_rate: number;
  mean_rank: number;
  company_recommendations: VisibilityRecommendationStrategy[];
  service_recommendations: VisibilityServiceRecommendation[];
  key_insights: string[];
  immediate_actions: string[];
}

export interface VisibilityRecommendationsRequest {
  domain: string;
}

export interface InfoCompletenessScoring {
  score: number;
  weight: number;
  rule_applied: string;
  findings: any; // keep flexible for nested object lists
  what_is_missing: string;
  how_to_improve: string;
  reasoning: string;
  max_score?: number;
  level?: 'high' | 'medium' | 'low' | 'error';
  status_title?: string;
  status_description?: string;
  findings_text?: string;
}

export interface TargetAudienceCompanyAnalysis {
  domain: string;
  page_urls_analyzed: string[];
  target_audience_identified: {
    primary_target_audience: string;
    confidence_level: string;
    evidence_strength: string;
  };
  target_audience_extraction: {
    explicit_statements: any[];
    implied_indicators: any[];
  };
  scoring_breakdown: Record<string, InfoCompletenessScoring>;
  final_calculation: string;
  final_score: number;
  max_final_score: number;
  overall_assessment: string;
  priority_improvements: any[];
}

export interface TargetAudienceProductAnalysisEntry {
  service_name: string;
  page_urls_analyzed: string[];
  target_audience_identified: {
    primary_target_audience: string;
    confidence_level: string;
    evidence_strength: string;
  };
  target_audience_extraction: {
    explicit_statements: Statement[];
    implied_indicators: Indicator[];
  };
  scoring_breakdown: any;
  final_calculation: string;
  final_score: number;
  max_final_score: number;
  overall_assessment: string;
  priority_improvements: any[];
}

export interface TargetAudienceResponse {
  company_analysis: TargetAudienceCompanyAnalysis;
  product_analyses: Record<string, TargetAudienceProductAnalysisEntry>;
  statusCode?: number;
}

export interface TargetAudienceRequest {
  domain: string;
  reportId?: string; // optional report context for history mode
}

export interface GeographicFocusCompanyAnalysis {
  domain: string;
  page_urls_analyzed: string[];
  geographic_focus_identified: {
    primary_geographic_focus: string;
    confidence_level: string;
    evidence_strength: string;
  };
  geographic_focus_extraction: {
    explicit_statements: Statement[];
    implied_indicators: Indicator[];
  };
  scoring_breakdown: Record<string, InfoCompletenessScoring>;
  final_calculation: string;
  final_score: number;
  overall_assessment: string;
  priority_improvements: any[];
}

export interface GeographicFocusProductAnalysisEntry
  extends Omit<GeographicFocusCompanyAnalysis, 'priority_improvements'> {
  service_name?: string;
  priority_improvements: any[];
}

export interface GeographicFocusResponse {
  company_analysis: GeographicFocusCompanyAnalysis;
  products: Record<string, GeographicFocusProductAnalysisEntry>;
  statusCode?: number;
}

export interface GeographicFocusRequest {
  domain: string;
  reportId?: string; // optional report context for history mode
}

export interface Statement {
  quote: string;
  page_url: string;
  context: string;
}

export interface Indicator {
  signal: string;
  page_url: string;
  reasoning: string;
}

export interface BusinessAddressResponse {
  domain?: string; // optional if backend injects
  page_urls_analyzed: string[];
  business_address_identified: {
    primary_business_address: string;
    confidence_level: string;
    evidence_strength: string;
  };
  business_address_extraction: {
    explicit_statements: Statement[];
    implied_indicators: Indicator[];
  };
  scoring_breakdown: Record<string, InfoCompletenessScoring>;
  final_calculation: string;
  final_score: number;
  overall_assessment: string;
  priority_improvements: any[];
  statusCode?: number;
}

export interface BusinessAddressRequest {
  domain: string;
  reportId?: string; // optional report context for history mode
}

export interface BusinessNameResponse {
  domain?: string;
  page_urls_analyzed: string[];
  business_name_identified: {
    primary_business_name: string;
    confidence_level: string;
    evidence_strength: string;
  };
  business_name_extraction: {
    explicit_statements: Statement[];
    implied_indicators: Indicator[];
  };
  scoring_breakdown: Record<string, InfoCompletenessScoring>;
  final_calculation: string;
  final_score: number;
  max_final_score?: number; // present in sample
  overall_assessment: string;
  priority_improvements: any[];
  statusCode?: number;
}

export interface BusinessNameRequest {
  domain: string;
  reportId?: string; // optional report context for history mode
}

// Pricing endpoint types
export interface PricingCustomerData {
  vat_rate: number;
  currency: string;
  payment_interval: number;
}

export interface PricingAmountBlock {
  ai_signal_core: number;
  ai_signal_domain: number;
}

export enum PaymentTypeEnum {
  CREDIT_CARD = 0,
  PAYPAL = 1,
}

export interface PricingDataResponse {
  customer_data: PricingCustomerData;
  base_prices: PricingAmountBlock;
  payment_interval_prices: PricingAmountBlock;
  prorated_discounts: PricingAmountBlock;
  paymentType?: PaymentTypeEnum;
}

// Submit Order (Purchase) types
export interface SubmitOrderRequest {
  ai_signal_core: string;
  ai_signal_domain: string[];
  payment_interval?: number;
  lang?: string; // optional language code for purchase context
}

export interface SubmitOrderResponse {
  success: boolean;
  order_id?: string;
  message?: string;
}

// Generate Report types
export interface GenerateReportRequest {
  domain: string;
  lang: string;
}

export interface GenerateReportResponse {
  accepted: boolean;
  domain?: string;
  message?: string;
}

// License Management types
export interface PlanLicenseInfo {
  total: number;
  assigned: number;
}

export interface LicenseDataResponse {
  core: PlanLicenseInfo;
  vision: PlanLicenseInfo;
}

export enum SubscriptionState {
  TERMINATED = -1,
  SUSPENDED = 0,
  ACTIVE = 1,
  CANCELING = -2, // Standalone: user cancelled, but stays active until the current billing period ends
}

export interface SubscriptionEntry {
  license: {
    id: number;
    plan: string;
    state: SubscriptionState;
    created_date: string;
    expiration_date: string;
    next_billing_date?: string;
    active_until?: string;
    cancel_url?: string;
    resume_url?: string;
  };
  domain?: {
    name: string;
    state: DomainStatus;
  };
}

export interface Count {
  total: number;
  assigned: number;
  unassigned?: number;
}

export interface SubInfo {
  store_url?: string;
}

export interface SubscriptionsResponse {
  subscriptions: SubscriptionEntry[];
  count: Count;
  info: SubInfo;
}

export enum BillingTransactionStatus {
  PAID = 'Paid',
  FAILED = 'Failed',
}

export interface BillingTransaction {
  invoice_number: string;
  date_created: string;
  subscription: string;
  payment_method: string;
  amount: number;
  currency: string;
  status: BillingTransactionStatus;
  invoice_url: string;
}

export interface BillingTransactionsRequest {
  search?: string;
  cursor?: string | null;
  limit?: number;
}

export interface BillingTransactionsResponse {
  transactions: BillingTransaction[];
  has_more: boolean;
  next_cursor: string | null;
  total_count: number;
  failed_count: number;
}

export interface AssignRequest {
  domain: string;
}

export interface TerminateLicenseRequest {
  domain: string;
  license_id: number;
}

export interface CancelLicenseRequest {
  license_id: number;
}

export interface CancelLicenseResponse {
  license: {
    id: number;
    state: SubscriptionState;
    active_until: string;
  };
}

export interface ResumeLicenseRequest {
  license_id: number;
}

export interface ResumeLicenseResponse {
  license: {
    id: number;
    state: SubscriptionState;
    next_billing_date: string;
  };
}

export interface AddDomainResponse {
  errors?: {
    domain: string[];
  };
}

export type Currency = 'USD' | 'EUR' | 'CHF';

export interface BillingSpendEntry {
  domain_id?: number;
  domain_name?: string;
  license_id: number;
  subscription: string;
  monthly_price: number;
  currency: Currency;
}

export interface BillingSpendResponse {
  websites: BillingSpendEntry[];
  total: number;
  currency: Currency;
}

export interface OrderPreviewLineItemRequest {
  product_id: number;
  quantity: number;
}

export type OrderPreviewRequest = OrderPreviewLineItemRequest[];

export interface OrderPreviewLineItem {
  product_id: number;
  quantity: number;
  amount: number;
  currency: Currency;
}

export interface OrderPreviewResponse {
  country: string;
  currency: Currency;
  line_items: OrderPreviewLineItem[];
  quantity: number;
  vat_rate: number;
  line_item_total: number;
  monthly_total_excl_vat: number;
  monthly_total_incl_vat: number;
  prorated_first_payment_excl_vat: number;
  prorated_first_payment_incl_vat: number;
  next_billing_date: string;
  raw_timestamp: number;
}

// Stripe checkout types
export type CreateCheckoutSessionRequest = OrderPreviewLineItemRequest[];

export interface CreateCheckoutSessionResponse {
  success: boolean;
  client_secret: string;
  session_id: string;
}

export interface BillingPortalSessionResponse {
  url: string;
}

export enum StripeLanguageEnum {
  EN = '1',
  GE = '2',
}

export type StripeLanguage = 'de' | 'en';
export interface UpdateStripeLanguage {
  lang_id: StripeLanguageEnum;
}

export interface UpdateStripeLanguageResponse {
  lang_id: StripeLanguageEnum;
  lang_code: StripeLanguage;
}

export type UserType = 'ka' | 'direct';
export interface UserTypeResponse {
  type: UserType;
}
